import React, { useState, useMemo } from "react";
import tenantService from "../../services/TenantService";
import RolePill from "../Common/RolePill";
import StatusPill from "../Common/StatusPill";
import { useUser } from "../../contexts/UserContext";
import {
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  AccordionSummary,
  AccordionDetails,
  Accordion,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Mail as MailIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Send as SendIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon
} from "@mui/icons-material";
import { OrganizationDTO, UserDTO, InvitationDTO, InvitationStatus } from "../../types/Tenant";

interface OrganizationUsersPanelProps {
  expanded: boolean;
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
  organization: OrganizationDTO | null;
  orgUsers: UserDTO[];
  orgInvitations: InvitationDTO[];
  isLoadingUsers: boolean;
  isLoadingInvitations: boolean;
  // Add a callback to refresh data after successful invitation
  onInvitationSent?: () => void;
}

const OrganizationUsersPanel: React.FC<OrganizationUsersPanelProps> = ({
  expanded,
  onChange,
  organization,
  orgUsers,
  orgInvitations,
  isLoadingUsers,
  isLoadingInvitations,
  onInvitationSent
}) => {
  const { user: currentUser } = useUser();
  const [inviteEmail, setInviteEmail] = useState<string>("");
  const [inviteRole, setInviteRole] = useState<string>("USER"); // Match the Role enum
  const [isSendingInvite, setIsSendingInvite] = useState<boolean>(false);
  const [inviteError, setInviteError] = useState<string>("");
  const [inviteSuccess, setInviteSuccess] = useState<string>("");
  const [isUpdatingRole, setIsUpdatingRole] = useState<boolean>(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editingUserRole, setEditingUserRole] = useState<string>("");
  const [resendingInvitations, setResendingInvitations] = useState<{ [key: string]: boolean }>({});
  const [cancelingInvitations, setCancelingInvitations] = useState<{ [key: string]: boolean }>({});
  const [showAllInvitations, setShowAllInvitations] = useState<boolean>(false);

  // Filter invitations based on showAllInvitations state
  const filteredInvitations = useMemo(() => {
    if (showAllInvitations) {
      return orgInvitations;
    } else {
      return orgInvitations.filter(invitation => 
        !invitation.status || invitation.status === InvitationStatus.PENDING
      );
    }
  }, [orgInvitations, showAllInvitations]);

  const handleInviteEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInviteEmail(e.target.value);
    setInviteError("");
    setInviteSuccess("");
  };

  const handleInviteRoleChange = (e: any) => {
    setInviteRole(e.target.value);
  };
  
  // Handle resending an invitation
  const handleResendInvitation = async (invitation: InvitationDTO) => {
    if (!organization?.id || !invitation.id) return;
    
    // Set loading state for this specific invitation
    setResendingInvitations(prev => ({
      ...prev,
      [invitation.id || '']: true
    }));
    
    try {
      // Call the API to resend invitation
      await tenantService.resendInvitation(organization.id, invitation.id);
      
      // Show success notification
      setInviteSuccess('Invitation resent successfully');
      setInviteError('');
      setTimeout(() => setInviteSuccess(''), 3000);
      
      // Update the invitation list
      if (onInvitationSent) {
        onInvitationSent();
      }
    } catch (error: any) {
      console.error('Error resending invitation:', error);
      setInviteError(error.response?.data?.detail || 'Failed to resend invitation');
      setTimeout(() => setInviteError(''), 3000);
    } finally {
      // Clear loading state
      setResendingInvitations(prev => ({
        ...prev,
        [invitation.id || '']: false
      }));
    }
  };
  
  // Handle canceling an invitation
  const handleCancelInvitation = async (invitation: InvitationDTO) => {
    if (!organization?.id || !invitation.id) return;
    
    // Set loading state for this specific invitation
    setCancelingInvitations(prev => ({
      ...prev,
      [invitation.id || '']: true
    }));
    
    try {
      // Call the API to rescind invitation
      await tenantService.rescindInvitation(organization.id, invitation.id);
      
      // Show success notification
      setInviteSuccess('Invitation canceled successfully');
      setInviteError('');
      setTimeout(() => setInviteSuccess(''), 3000);
      
      // Update the invitation list
      if (onInvitationSent) {
        onInvitationSent();
      }
    } catch (error: any) {
      console.error('Error canceling invitation:', error);
      setInviteError(error.response?.data?.detail || 'Failed to cancel invitation');
      setTimeout(() => setInviteError(''), 3000);
    } finally {
      // Clear loading state
      setCancelingInvitations(prev => ({
        ...prev,
        [invitation.id || '']: false
      }));
    }
  };
  
  // Handle changing a user's role
  const handleEditUserRole = (userId: string, currentRole: string) => {
    // Don't allow users to edit their own role
    if (userId === currentUser?.id) {
      return;
    }
    setEditingUserId(userId);
    setEditingUserRole(currentRole);
  };
  
  // Cancel role editing
  const handleCancelRoleEdit = () => {
    setEditingUserId(null);
    setEditingUserRole("");
  };
  
  // Save the new role
  const handleSaveRole = async (userId: string, newRole: string) => {
    if (!organization?.id || !userId) return;
    
    setIsUpdatingRole(true);
    try {
      // Call API to update user role
      // This is a placeholder - you'll need to implement the API call in TenantService
      // await tenantService.updateUserRole(organization.id, userId, newRole);
      
      // For now, just simulate success
      setTimeout(() => {
        setEditingUserId(null);
        setEditingUserRole("");
        
        // Refresh user list
        if (onInvitationSent) {
          onInvitationSent(); // Reuse this callback to refresh the user list
        }
      }, 500);
    } catch (error) {
      console.error("Failed to update user role", error);
    } finally {
      setIsUpdatingRole(false);
    }
  };
  
  // Check if the user is the current logged-in user by comparing both ID and email
  const isCurrentUser = (user: UserDTO) => {
    if (!user || !currentUser) return false;
    
    // Check if either ID or email matches the current user
    return (
      (user.id && user.id === currentUser.id) || 
      (user.email && currentUser.email && user.email.toLowerCase() === currentUser.email.toLowerCase())
    );
  };
  
  const handleSendInvitation = async () => {
    // Validate form
    if (!inviteEmail) {
      setInviteError("Email address is required");
      return;
    }
    
    if (!organization?.id) {
      setInviteError("Organization ID is missing");
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      setInviteError("Please enter a valid email address");
      return;
    }
    
    setIsSendingInvite(true);
    setInviteError("");
    
    try {
      // Create invitation object
      const invitationData: InvitationDTO = {
        email: inviteEmail,
        organization_id: organization.id,
        role: inviteRole as any // Cast to match the Role enum
      };
      
      // Send invitation via TenantService
      await tenantService.sendInvitation(organization.id, invitationData);
      
      // Reset form and show success
      setInviteEmail("");
      setInviteSuccess("Invitation sent successfully");
      
      // Refresh the invitations list if callback provided
      if (onInvitationSent) {
        onInvitationSent();
      }
    } catch (error: any) {
      console.error("Error sending invitation:", error);
      setInviteError(error.response?.data?.detail || "Failed to send invitation");
    } finally {
      setIsSendingInvite(false);
    }
  };

  return (
    <Accordion 
      expanded={expanded} 
      onChange={onChange}
      sx={{ mb: 2, borderRadius: '8px', overflow: 'hidden' }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="users-content"
        id="users-header"
        sx={{ backgroundColor: '#f5f5f5' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PeopleIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">Users</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 3 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Manage user access and permissions for this organization.
        </Typography>
        
        {/* Current Organization Users Section */}
        <Typography variant="h6" gutterBottom>
          <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Current Users
        </Typography>
        
        {isLoadingUsers ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress />
          </Box>
        ) : orgUsers.length > 0 ? (
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orgUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      {user.first_name} {user.last_name}
                      {isCurrentUser(user) && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          (You)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {editingUserId === user.id ? (
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <Select
                            value={editingUserRole}
                            onChange={(e) => setEditingUserRole(e.target.value)}
                            size="small"
                            displayEmpty
                          >
                            <MenuItem value="ADMIN">Admin</MenuItem>
                            <MenuItem value="USER">User</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <RolePill role={user.role || 'USER'} />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {/* Display edit/save actions for non-current users */}
                      {!isCurrentUser(user) && (
                        <>
                          {editingUserId === user.id ? (
                            <>
                              <IconButton 
                                size="small" 
                                color="primary" 
                                title="Save role"
                                onClick={() => handleSaveRole(user.id || '', editingUserRole)}
                                disabled={isUpdatingRole}
                              >
                                {isUpdatingRole ? <CircularProgress size={20} /> : <SaveIcon fontSize="small" />}
                              </IconButton>
                              <IconButton 
                                size="small" 
                                title="Cancel"
                                onClick={handleCancelRoleEdit}
                                disabled={isUpdatingRole}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <>
                              <IconButton 
                                size="small" 
                                color="primary" 
                                title="Edit role"
                                onClick={() => handleEditUserRole(user.id || '', user.role || 'USER')}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                color="error" 
                                title="Remove user"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </>
                      )}
                      {/* No edit options for current user */}
                      {isCurrentUser(user) && (
                        <Typography variant="caption" color="text.secondary">
                          Cannot modify self
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info" sx={{ mb: 3 }}>
            No users have been added to this organization yet.
          </Alert>
        )}
        
        {/* Invitations Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            <MailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {showAllInvitations ? 'All Invitations' : 'Pending Invitations'}
          </Typography>
          
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowAllInvitations(!showAllInvitations)}
            startIcon={showAllInvitations ? <FilterListIcon /> : <VisibilityIcon />}
          >
            {showAllInvitations ? 'Show Pending Only' : 'Show All'}
          </Button>
        </Box>
        
        {/* Invitations Loading State */}
        {isLoadingInvitations ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress />
          </Box>
        ) : filteredInvitations.length > 0 ? (
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  {showAllInvitations && <TableCell>Status</TableCell>}
                  <TableCell>Date Sent</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredInvitations.map((invitation) => (
                  <TableRow key={invitation.id}>
                    <TableCell>{invitation.email}</TableCell>
                    <TableCell>
                      <RolePill role={invitation.role} />
                    </TableCell>
                    {showAllInvitations && (
                      <TableCell>
                        <StatusPill status={invitation.status || InvitationStatus.PENDING} />
                      </TableCell>
                    )}
                    <TableCell>
                      {invitation.sent_at ? new Date(invitation.sent_at).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell align="right">
                      {/* Only show action buttons for pending invitations */}
                      {(!invitation.status || invitation.status === InvitationStatus.PENDING) && (
                        <>
                          <Tooltip title="Resend invitation">
                            <IconButton 
                              size="small" 
                              color="primary" 
                              onClick={() => handleResendInvitation(invitation)}
                              disabled={resendingInvitations[invitation.id || '']}
                            >
                              {resendingInvitations[invitation.id || ''] ? 
                                <CircularProgress size={20} /> : 
                                <RefreshIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel invitation">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleCancelInvitation(invitation)}
                              disabled={resendingInvitations[invitation.id || ''] || cancelingInvitations[invitation.id || '']}
                            >
                              {cancelingInvitations[invitation.id || ''] ? 
                                <CircularProgress size={20} /> : 
                                <CancelIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      
                      {/* For non-pending invitations, show status text */}
                      {invitation.status && invitation.status !== InvitationStatus.PENDING && (
                        <Typography variant="caption" color="text.secondary">
                          No actions available
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info" sx={{ mb: 3 }}>
            {showAllInvitations ? 'No invitations found.' : 'No pending invitations.'}
          </Alert>
        )}
        
        {/* Invite New User Section */}
        <Box sx={{ mt: 4, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            <AddIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Invite New User
          </Typography>
          
          <Paper variant="outlined" sx={{ p: 3 }}>
            {inviteError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {inviteError}
              </Alert>
            )}
            
            {inviteSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {inviteSuccess}
              </Alert>
            )}
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email Address"
                  variant="outlined"
                  fullWidth
                  placeholder="email@example.com"
                  value={inviteEmail}
                  onChange={handleInviteEmailChange}
                  error={!!inviteError}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    label="Role"
                    value={inviteRole}
                    onChange={handleInviteRoleChange}
                  >
                    <MenuItem value="ADMIN">Admin</MenuItem>
                    <MenuItem value="USER">Member</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center' }}>
                <Button 
                  variant="contained" 
                  fullWidth
                  startIcon={isSendingInvite ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  disabled={!organization?.id || !inviteEmail || isSendingInvite}
                  onClick={handleSendInvitation}
                >
                  {isSendingInvite ? 'Sending...' : 'Invite'}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default OrganizationUsersPanel;
